import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Crown, Clock3, Globe2, Users, Film as FilmIcon, ChevronLeft, X, Bookmark, Sparkles, ShieldCheck, ExternalLink, Lock } from "lucide-react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function FilmDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [film, setFilm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("overview");
    const [playing, setPlaying] = useState(false);
    const [watched, setWatched] = useState(false);
    const [val, setVal] = useState({ loading: false, data: null });
    const [chain, setChain] = useState([]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/films/${id}`);
                setFilm(data);
                // track view (fire and forget)
                api.post(`/films/${id}/view`).catch(() => {});
                // chain-of-title timeline
                api.get(`/chain-of-title/${id}`).then(r => setChain(r.data.entries || [])).catch(() => {});
                // watchlist check (buyers only)
                if (user?.role === "buyer") {
                    api.get("/watchlist").then(r => {
                        setWatched(!!r.data.find(w => w.film?.id === id));
                    }).catch(() => {});
                }
            } catch (e) {
                setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [id, user?.role]);

    const toggleWatchlist = async () => {
        try {
            if (watched) {
                await api.delete(`/watchlist/${id}`);
                setWatched(false);
                toast.success("Removed from watchlist");
            } else {
                await api.post("/watchlist", { film_id: id });
                setWatched(true);
                toast.success("Added to watchlist");
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const runValuation = async () => {
        if (!film) return;
        setVal({ loading: true, data: null });
        try {
            const { data } = await api.post("/ai/valuation", {
                title: film.title,
                synopsis: film.synopsis,
                genres: film.genres || [],
                country: film.country,
                language: film.language,
                budget_usd: film.budget_usd,
                box_office_usd: film.box_office_usd,
                awards: film.awards || [],
                cast: film.cast || [],
                stage: film.stage || "Completed",
                rights_type: film.rights_type || [],
            });
            setVal({ loading: false, data });
        } catch (e) {
            setVal({ loading: false, data: null });
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "AI valuation failed");
        }
    };

    const [screener, setScreener] = useState(null);
    const playTrailerWithWatermark = async () => {
        setPlaying(true);
        // Fetch forensic stamp in parallel (only if we haven't already)
        if (!screener) {
            try {
                const { data } = await api.get(`/screener/watermark/${id}`);
                setScreener(data);
            } catch (_) {}
        }
    };

    const requestOwnership = async () => {
        if (!user) {
            nav(`/login?next=/film/${id}`);
            return;
        }
        if (user.role !== "buyer") {
            alert("Only buyers can open a deal room. Log in as a buyer.");
            return;
        }
        try {
            const { data } = await api.post("/deals", { film_id: id });
            nav(`/deal/${data.id}`);
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-ink-300">
                <Navbar />
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 animate-pulse text-silver-500">
                    Loading…
                </div>
            </div>
        );
    }
    if (err || !film) {
        return (
            <div className="min-h-screen bg-ink-300">
                <Navbar />
                <div className="max-w-[900px] mx-auto px-6 py-32 text-center text-silver-400">
                    {err || "Film not found"}.{" "}
                    <Link to="/catalog" className="text-platinum">
                        Back to catalog
                    </Link>
                </div>
            </div>
        );
    }

    const statusLabel =
        film.rights_status === "under_offer"
            ? "Under Offer"
            : film.rights_status === "sold"
              ? "Sold"
              : "Available";

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            {/* HERO */}
            <section className="relative min-h-[80vh] overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    {film.poster_url ? (
                        <motion.img
                            initial={{ scale: 1.05 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 2 }}
                            src={film.poster_url}
                            alt={film.title}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-ink-200 to-ink-400" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-ink-300 via-ink-300/85 to-ink-300/10 pointer-events-none" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-300 via-transparent to-ink-300/30 pointer-events-none" />
                </div>

                <div className="max-w-[1400px] mx-auto px-4 md:px-10 pt-8 md:pt-12">
                    <button
                        onClick={() => nav(-1)}
                        className="inline-flex items-center gap-2 text-sm text-silver-400 hover:text-platinum"
                        data-testid="back-btn"
                    >
                        <ChevronLeft className="w-4 h-4" /> Back
                    </button>
                </div>

                <div className="max-w-[1400px] mx-auto px-4 md:px-10 pt-10 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-[1.2fr_1fr] gap-8 md:gap-14 items-end min-h-[60vh] md:min-h-[64vh]">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="flex items-center gap-2 md:gap-3 text-[10px] md:text-[11px] tracking-[0.3em] uppercase mb-3 md:mb-4 flex-wrap">
                            <span className="text-platinum">{film.country}</span>
                            <span className="w-2 h-px bg-silver-500" />
                            <span className="text-silver-400">{film.language}</span>
                            <span className="w-2 h-px bg-silver-500" />
                            <span
                                className={
                                    film.rights_status === "available"
                                        ? "text-emerald-400"
                                        : film.rights_status === "sold"
                                          ? "text-silver-400"
                                          : "text-platinum"
                                }
                            >
                                {statusLabel}
                            </span>
                        </div>
                        <h1 className="font-display text-[2.75rem] sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
                            {film.title}
                        </h1>
                        <p className="mt-4 md:mt-6 text-silver-300 max-w-2xl leading-relaxed text-sm md:text-base">
                            {film.synopsis}
                        </p>

                        <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3 md:gap-4">
                            <button
                                onClick={playTrailerWithWatermark}
                                className="inline-flex items-center gap-2 glass-strong border-white/20 px-4 md:px-5 py-2.5 md:py-3 rounded-sm hover:border-platinum text-white text-sm"
                                data-testid="play-trailer-btn"
                            >
                                <Play className="w-4 h-4 text-platinum" /> Play Trailer
                            </button>
                            {film.auction_enabled && (
                                <Link
                                    to={`/auction/${id}`}
                                    className="inline-flex items-center gap-2 border border-platinum text-platinum hover:bg-platinum/10 px-4 md:px-5 py-2.5 md:py-3 rounded-sm text-sm animate-platinum-pulse"
                                    data-testid="goto-auction-btn"
                                >
                                    Live Auction →
                                </Link>
                            )}
                            {!user ? (
                                <Link
                                    to={`/login?next=/film/${id}`}
                                    className="inline-flex items-center gap-2 bg-platinum hover:bg-white text-ink-300 font-medium px-4 md:px-6 py-2.5 md:py-3 rounded-sm uppercase tracking-widest text-[11px] md:text-xs"
                                    data-testid="login-to-buy-cta"
                                >
                                    <Crown className="w-4 h-4" /> Sign in to view price & buy
                                </Link>
                            ) : user.role === "buyer" ? (
                                <>
                                    <button
                                        onClick={requestOwnership}
                                        disabled={film.rights_status === "sold"}
                                        className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 disabled:opacity-40 disabled:cursor-not-allowed text-ink-300 font-medium px-4 md:px-6 py-2.5 md:py-3 rounded-sm uppercase tracking-widest text-[11px] md:text-xs animate-platinum-pulse"
                                        data-testid="request-ownership-btn"
                                    >
                                        <Crown className="w-4 h-4" />
                                        {film.rights_status === "sold" ? "Transferred" : "Request Exclusive Ownership"}
                                    </button>
                                    <button
                                        onClick={toggleWatchlist}
                                        className={`inline-flex items-center gap-2 glass-strong border px-4 py-2.5 rounded-sm text-sm transition-colors ${
                                            watched ? "border-platinum text-platinum" : "border-white/20 text-silver-200 hover:border-platinum"
                                        }`}
                                        data-testid="watchlist-toggle-btn"
                                    >
                                        <Bookmark className={`w-4 h-4 ${watched ? "fill-current" : ""}`} />
                                        {watched ? "Watchlisted" : "Watchlist"}
                                    </button>
                                </>
                            ) : user.role === "creator" ? (
                                <div className="inline-flex items-center gap-2 border border-white/15 text-silver-300 px-4 py-2.5 rounded-sm text-xs tracking-widest uppercase" data-testid="creator-viewing-note">
                                    <Crown className="w-3.5 h-3.5 text-platinum" />
                                    Viewing as creator · buyers see purchase CTA here
                                </div>
                            ) : (
                                <div className="inline-flex items-center gap-2 border border-white/15 text-silver-300 px-4 py-2.5 rounded-sm text-xs tracking-widest uppercase">
                                    <ShieldCheck className="w-3.5 h-3.5 text-platinum" /> Admin view
                                </div>
                            )}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="glass p-6 md:p-8 rounded-sm"
                    >
                        <div className="text-xs uppercase tracking-widest text-silver-400 mb-2">
                            Asking price
                        </div>
                        {user ? (
                            <div className="font-display text-4xl md:text-5xl platinum-text mb-5 md:mb-6" data-testid="film-price">
                                ${Number(film.price_usd || 0).toLocaleString()}
                            </div>
                        ) : (
                            <div className="mb-5 md:mb-6" data-testid="film-price-gated">
                                <div className="font-display text-3xl md:text-4xl text-silver-500/60 tracking-wide select-none" aria-hidden>
                                    $•,•••,•••
                                </div>
                                <Link to={`/login?next=/film/${id}`} className="mt-2 inline-flex items-center gap-1.5 text-platinum text-[11px] tracking-widest uppercase hover:underline">
                                    <Lock className="w-3 h-3" /> Sign in to view price
                                </Link>
                            </div>
                        )}
                        <dl className="space-y-3 text-sm">
                            <Row icon={Clock3} label="Runtime" value={`${film.runtime} min`} />
                            <Row icon={Globe2} label="Country / Language" value={`${film.country} · ${film.language}`} />
                            <Row icon={FilmIcon} label="Genres" value={(film.genres || []).concat(film.sub_genres || []).join(" · ")} />
                            <Row icon={Users} label="Cast" value={(film.cast || []).slice(0, 3).join(", ")} />
                            {film.content_type && (
                                <Row icon={FilmIcon} label="Format" value={`${film.content_type} · ${film.stage || "Completed"}`} />
                            )}
                            {film.region && (
                                <Row icon={Globe2} label="Territory" value={film.region} />
                            )}
                        </dl>

                        {film.rights_type && film.rights_type.length > 0 && (
                            <div className="mt-5 pt-5 border-t border-white/5">
                                <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-2">
                                    Rights on offer
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {film.rights_type.map((r) => (
                                        <span key={r} className="text-[11px] tracking-wide bg-platinum/10 border border-platinum/30 text-platinum px-2 py-1 rounded-sm">
                                            {r}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-5 pt-5 border-t border-white/5 grid grid-cols-2 gap-3 text-xs">
                            {film.copyright_status && (
                                <Badge label="Copyright" value={film.copyright_status} good={film.copyright_status === "Registered"} />
                            )}
                            <Badge
                                label="Chain of title"
                                value={film.chain_of_title_verified ? "Verified" : "Unverified"}
                                good={film.chain_of_title_verified}
                            />
                            <Badge
                                label="Encumbrances"
                                value={film.encumbrances ? "Present" : "None"}
                                good={!film.encumbrances}
                            />
                            {film.exclusivity && (
                                <Badge label="Exclusivity" value={film.exclusivity} good={film.exclusivity === "Exclusive"} />
                            )}
                        </div>

                        <div className="mt-6 pt-6 border-t border-white/5 text-xs text-silver-500 leading-relaxed">
                            Worldwide, perpetual, exclusive copyright transfer governed by
                            the Indian Copyright Act, 1957. Chain of title verified.
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* TABS */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28">
                <div className="flex items-center gap-6 border-b border-white/5 mb-10 text-sm uppercase tracking-widest">
                    {[
                        ["overview", "Overview"],
                        ["cast", "Cast & Crew"],
                        ["ledger", "Ledger"],
                        ["ai", "AI Intelligence"],
                        ["legal", "Legal"],
                    ].map(([k, l]) => (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            className={`py-4 border-b-2 transition-colors ${
                                tab === k
                                    ? "border-platinum text-platinum"
                                    : "border-transparent text-silver-500 hover:text-white"
                            }`}
                            data-testid={`tab-${k}`}
                        >
                            {l}
                        </button>
                    ))}
                </div>

                {tab === "overview" && (
                    <div className="grid md:grid-cols-3 gap-10 text-silver-300">
                        <div className="md:col-span-2">
                            <h3 className="font-display text-2xl mb-3">Synopsis</h3>
                            <p className="leading-relaxed">{film.synopsis}</p>

                            {(film.box_office_usd ||
                                film.revenue_history_usd ||
                                film.streaming_performance ||
                                (film.awards && film.awards.length > 0) ||
                                film.distribution_deals) && (
                                <>
                                    <h3 className="font-display text-2xl mt-10 mb-3">
                                        Commercial performance
                                    </h3>
                                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                                        {film.box_office_usd != null && (
                                            <Metric label="Box office" value={`$${Number(film.box_office_usd).toLocaleString()}`} />
                                        )}
                                        {film.revenue_history_usd != null && (
                                            <Metric label="Lifetime revenue" value={`$${Number(film.revenue_history_usd).toLocaleString()}`} />
                                        )}
                                        {film.budget_usd != null && (
                                            <Metric label="Production budget" value={`$${Number(film.budget_usd).toLocaleString()}`} />
                                        )}
                                        {film.streaming_performance && (
                                            <Metric label="Streaming" value={film.streaming_performance} />
                                        )}
                                        {film.distribution_deals && (
                                            <Metric label="Existing deals" value={film.distribution_deals} full />
                                        )}
                                    </div>

                                    {film.awards && film.awards.length > 0 && (
                                        <>
                                            <h4 className="font-display text-xl mt-8 mb-2">
                                                Awards & Festival
                                            </h4>
                                            <ul className="space-y-1 text-sm">
                                                {film.awards.map((a) => (
                                                    <li key={a} className="flex items-start gap-2">
                                                        <span className="text-platinum">◆</span>
                                                        {a}
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}

                                    {film.attached_talent && film.attached_talent.length > 0 && (
                                        <>
                                            <h4 className="font-display text-xl mt-8 mb-2">
                                                Attached talent
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {film.attached_talent.map((t) => (
                                                    <span
                                                        key={t}
                                                        className="text-xs bg-white/5 border border-white/10 rounded-sm px-3 py-1.5"
                                                    >
                                                        {t}
                                                    </span>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        <div>
                            <h3 className="font-display text-2xl mb-3">Chain of Title</h3>
                            <ul className="space-y-2 text-sm">
                                {(film.chain_of_title_docs || []).map((d) => (
                                    <li key={d} className="flex items-center justify-between border-b border-white/5 py-2">
                                        <span>{d}</span>
                                        <span className="text-emerald-400 text-xs uppercase tracking-widest">
                                            Verified
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {tab === "cast" && (
                    <div className="grid md:grid-cols-2 gap-10">
                        <div>
                            <h3 className="font-display text-2xl mb-4">Cast</h3>
                            <div className="grid grid-cols-2 gap-3 text-silver-200">
                                {(film.cast || []).map((c) => (
                                    <div key={c} className="glass p-3 rounded-sm">
                                        {c}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h3 className="font-display text-2xl mb-4">Crew</h3>
                            <div className="space-y-2 text-silver-300 text-sm">
                                {(film.crew || []).map((c) => (
                                    <div key={c} className="border-b border-white/5 pb-2">
                                        {c}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {tab === "ledger" && (
                    <div className="max-w-3xl" data-testid="ledger-tab">
                        <h3 className="font-display text-2xl mb-6">On-chain provenance</h3>
                        <p className="text-silver-400 text-sm mb-8">
                            Every deed, contract and ownership transfer is SHA-256 hashed and
                            appended to an immutable ledger. Share any hash as public proof of
                            chain-of-title.
                        </p>
                        {chain.length === 0 ? (
                            <div className="text-silver-500 text-sm">No ledger entries yet for this film.</div>
                        ) : (
                            <ol className="space-y-5">
                                {chain.map((e, i) => (
                                    <li key={e.id} className="relative pl-8 border-l border-white/10">
                                        <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full border border-platinum/70 bg-ink-300" />
                                        <div className="text-[11px] tracking-[0.3em] uppercase text-silver-500 mb-1">
                                            {new Date(e.created_at).toLocaleString()} · {e.kind.replace("_", " ")}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs">
                                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
                                            <Link to={`/verify/${e.hash}`} className="text-silver-200 hover:text-platinum font-mono break-all">
                                                {e.hash.slice(0, 24)}…{e.hash.slice(-8)}
                                            </Link>
                                            <ExternalLink className="w-3 h-3 text-silver-500" />
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>
                )}

                {tab === "ai" && (
                    <div className="max-w-3xl" data-testid="ai-tab">
                        <div className="flex items-center gap-3 mb-4">
                            <Sparkles className="w-5 h-5 text-platinum" />
                            <h3 className="font-display text-2xl">AI rights valuation</h3>
                        </div>
                        <p className="text-silver-400 text-sm mb-6">
                            Get an instant fair-value range for exclusive copyright transfer, based on
                            genre, budget, festivals and comparable recent deals. Powered by Claude Sonnet 4.5.
                        </p>
                        {!val.data && (
                            <button
                                onClick={runValuation}
                                disabled={val.loading}
                                className="bg-platinum text-ink-400 px-5 py-3 text-sm tracking-widest uppercase hover:bg-white disabled:opacity-50"
                                data-testid="ai-valuation-btn"
                            >
                                {val.loading ? "Analysing…" : "Run AI Valuation"}
                            </button>
                        )}
                        {val.data && (
                            <div className="space-y-6">
                                <div className="bg-ink-200/60 border border-white/10 rounded-sm p-6">
                                    <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-3">
                                        Suggested fair range · {val.data.confidence} confidence
                                    </div>
                                    <div className="font-display text-4xl md:text-5xl silver-text" data-testid="ai-val-range">
                                        ${Number(val.data.low_usd).toLocaleString()} – ${Number(val.data.high_usd).toLocaleString()}
                                    </div>
                                    <p className="mt-5 text-silver-300 text-sm leading-relaxed">{val.data.rationale}</p>
                                </div>
                                <div>
                                    <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-3">Comparable deals</div>
                                    <div className="space-y-3">
                                        {(val.data.comparables || []).map((c, i) => (
                                            <div key={i} className="flex items-center justify-between border-b border-white/5 py-3 text-sm">
                                                <div>
                                                    <div className="text-silver-200">{c.title}</div>
                                                    <div className="text-silver-500 text-xs">{c.country} · {c.year} · {c.note}</div>
                                                </div>
                                                <div className="text-platinum font-mono">${Number(c.deal_usd).toLocaleString()}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "legal" && (
                    <div className="glass p-8 rounded-sm text-silver-300 leading-relaxed text-sm max-w-3xl">
                        <p>
                            Title held by Silver Screen Xchange under a duly executed Creator
                            Assignment Deed (Indian Copyright Act, 1957, Section 18).
                            Purchase Agreement is an exclusive, worldwide, perpetual and
                            irrevocable transfer of copyright to the verified Buyer, subject
                            to receipt of consideration. Indemnity period: seven (7) years.
                            Jurisdiction: Mumbai, Maharashtra.
                        </p>
                    </div>
                )}
            </section>

            {/* TRAILER MODAL */}
            <AnimatePresence>
                {playing && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
                        onClick={() => setPlaying(false)}
                        data-testid="trailer-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            transition={{ duration: 0.35 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-[1100px]"
                        >
                            <div className="relative aspect-video bg-black rounded-sm overflow-hidden border border-white/10">
                                <button
                                    onClick={() => setPlaying(false)}
                                    className="absolute top-3 right-3 z-20 w-10 h-10 rounded-full bg-black/70 hover:bg-black border border-white/20 flex items-center justify-center text-white"
                                    data-testid="trailer-close-btn"
                                    aria-label="Close trailer"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                                {film.trailer_youtube_id ? (
                                    <iframe
                                        title={`${film.title} trailer`}
                                        src={`https://www.youtube.com/embed/${film.trailer_youtube_id}?autoplay=1&rel=0&modestbranding=1`}
                                        className="w-full h-full"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        data-testid="trailer-player"
                                    />
                                ) : film.trailer_url ? (
                                    <video
                                        src={film.trailer_url}
                                        controls
                                        autoPlay
                                        playsInline
                                        className="w-full h-full object-contain bg-black"
                                        data-testid="trailer-player"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-silver-400">
                                        No trailer available.
                                    </div>
                                )}
                                {/* Forensic screener watermark — tiled diagonal overlay */}
                                {screener && (
                                    <div
                                        className="absolute inset-0 pointer-events-none select-none overflow-hidden z-10 mix-blend-overlay"
                                        data-testid="forensic-watermark"
                                        aria-hidden
                                    >
                                        <div
                                            className="absolute -inset-1/2 flex flex-wrap content-start text-white/18 text-[11px] tracking-widest uppercase gap-x-16 gap-y-24 p-8"
                                            style={{ transform: "rotate(-22deg)", fontFamily: "ui-monospace, monospace" }}
                                        >
                                            {Array.from({ length: 60 }).map((_, i) => (
                                                <span key={i} className="whitespace-nowrap">
                                                    {screener.stamp} · {screener.hash.slice(0, 12)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {screener && (
                                <div className="mt-3 text-[10px] tracking-widest uppercase text-silver-500 flex items-center gap-2" data-testid="watermark-caption">
                                    <ShieldCheck className="w-3 h-3 text-platinum" /> Screener stamped to you · {screener.hash.slice(0, 16)}…
                                </div>
                            )}
                            {film.trailer_youtube_id && (
                                <div className="mt-3 flex items-center justify-between gap-3 text-xs text-silver-400">
                                    <span className="tracking-widest uppercase">
                                        If playback is blocked by the rights-holder
                                    </span>
                                    <a
                                        href={`https://www.youtube.com/watch?v=${film.trailer_youtube_id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 text-ink-300 px-4 py-2 rounded-sm uppercase tracking-widest text-[11px] font-medium"
                                        data-testid="trailer-youtube-link"
                                    >
                                        Watch on YouTube ↗
                                    </a>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function Row({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="w-4 h-4 mt-1 text-platinum" />
            <div>
                <div className="text-silver-500 text-[11px] uppercase tracking-widest">
                    {label}
                </div>
                <div className="text-silver-100">{value}</div>
            </div>
        </div>
    );
}

function Badge({ label, value, good }) {
    return (
        <div
            className={`border rounded-sm p-2.5 ${
                good
                    ? "border-emerald-400/30 bg-emerald-400/5"
                    : "border-amber-400/30 bg-amber-400/5"
            }`}
        >
            <div className="text-[9px] uppercase tracking-widest text-silver-500">
                {label}
            </div>
            <div className={good ? "text-emerald-300 text-sm" : "text-amber-300 text-sm"}>
                {value}
            </div>
        </div>
    );
}

function Metric({ label, value, full }) {
    return (
        <div className={`border border-white/5 bg-ink-200/40 rounded-sm p-3 ${full ? "sm:col-span-2" : ""}`}>
            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-1">
                {label}
            </div>
            <div className="text-silver-100">{value}</div>
        </div>
    );
}
